import React, { useEffect, useRef, useState, useMemo } from "react";
import { useFormik } from "formik";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  Grid,
  Paper,
  Box,
  MenuItem,
  Autocomplete,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { Close } from "@mui/icons-material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import type { FilterOptionsState } from "@mui/material/useAutocomplete";
import { Snackbar, Alert } from "@mui/material";
import { ReactComponent as PhotoIcon } from "../../../assets/icons/photo-icon.svg";
import { userProfileValidationSchema } from "../validationSchema";
import { AntSwitch } from "../../become-provider-page/components/partner-registration-form/partner-registration-form.utils";
import {
  deleteUserPhoto,
  updateUserProfile,
  uploadUserPhoto,
  createUserProfile,
} from "../../../api/userProfile.api";
import {
  addressData,
  extractAllOptions,
  filterOptionsByValue,
} from "../../../utils/extractCityStateZipCode";
import { updateSessionDetails } from "../../../common/routines/user-operations";
import "./UserProfileForm.scss";
import { useDispatch } from "react-redux";
import { useDebounce } from "use-debounce";
// import { checkExistingUser } from "../../../api/auth";

type Option = { value: string; label: string };
type PermanentAddress = {
  street: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
};

const UserProfileForm = ({
  isProfileCreated,
  setIsEditMode,
  userProfileDetails,
  setUserProfileDetails,
  fetchUserProfileDetails,
  prefilledBillingDetails,
}: any) => {
  // STATES
  const [sameAsPermanent, setSameAsPermanent] = useState(
    userProfileDetails?.billingSameAsPermanent || false
  );
  const [preview, setPreview] = useState<string | null>(
    userProfileDetails?.profile_photo || null
  );
  const [uploading, setUploading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  // const [emailAvailabilityError, setEmailAvailabilityError] = useState("");
  // const [phoneAvailabilityError, setPhoneAvailabilityError] = useState("");

  const dispatch = useDispatch();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const allAddressOptions = extractAllOptions(addressData);

  const defaultFilter = createFilterOptions<Option>({
    matchFrom: "any",
    stringify: (o) => `${o.label} ${o.value}`,
  });

  const limitFilterOptions = (
    options: Option[],
    state: FilterOptionsState<Option>
  ) => {
    const hasQuery = (state.inputValue || "").trim().length > 0;
    const filtered = hasQuery ? defaultFilter(options, state) : options;
    return filtered.slice(0, hasQuery ? 50 : 10);
  };

  // Compute if permanent address has data
  const hasPermanentData = useMemo(() => {
    const addr = userProfileDetails?.permanentAddress;
    return Boolean(
      addr &&
      (addr.street?.trim() ||
        addr.city?.trim() ||
        addr.state?.trim() ||
        addr.country?.trim() ||
        addr.zipcode?.trim())
    );
  }, [userProfileDetails?.permanentAddress]);

  // Compute if billing address has data (independent of sameAsPermanent)
  const hasBillingData = useMemo(() => {
    const addr = userProfileDetails?.billingAddress;
    return Boolean(
      addr &&
      (addr.street?.trim() ||
        addr.city?.trim() ||
        addr.state?.trim() ||
        addr.country?.trim() ||
        addr.zipcode?.trim())
    );
  }, [userProfileDetails?.billingAddress]);

  // Start with switches based on loaded data
  const [enablePermanentAddress, setEnablePermanentAddress] =
    useState(hasPermanentData);
  const [enableBillingAddress, setEnableBillingAddress] = useState(
    (hasBillingData || !!prefilledBillingDetails) && !userProfileDetails?.billingSameAsPermanent
  );

  // Computed initial billing address: if sameAsPermanent, use permanent's data
  const computedInitialBillingAddress = useMemo(() => {
    if (userProfileDetails?.billingSameAsPermanent && hasPermanentData) {
      return {
        ...userProfileDetails.permanentAddress,
      };
    }

    if (prefilledBillingDetails && !hasBillingData) {
      return {
        street: prefilledBillingDetails.street || "",
        city: prefilledBillingDetails.city || "",
        state: prefilledBillingDetails.state || "",
        country: prefilledBillingDetails.country || "",
        zipcode: prefilledBillingDetails.zipcode || "",
      };
    }

    return (
      userProfileDetails?.billingAddress || {
        street: "",
        city: "",
        state: "",
        country: "",
        zipcode: "",
      }
    );
  }, [userProfileDetails, hasPermanentData, prefilledBillingDetails, hasBillingData]);

  const initialValues = useMemo(
    () => ({
      firstName: userProfileDetails?.first_name || "",
      lastName: userProfileDetails?.last_name || "",
      email: userProfileDetails?.email || "",
      phoneNumber: userProfileDetails?.mobileNumber || "",
      isFuneralDirector: userProfileDetails?.isFuneralDirector === true
        ? "IamaFuneralDirector"
        : userProfileDetails?.isFuneralDirector === false
          ? "BuyingforSelf/Family"
          : "",
      permanentAddress: userProfileDetails?.permanentAddress || {
        street: "",
        city: "",
        state: "",
        country: "",
        zipcode: "",
      },
      billingAddress: computedInitialBillingAddress,
      profile_photo: "",
    }),
    [userProfileDetails, computedInitialBillingAddress]
  );

  const disableLockedFields = {
    firstName: Boolean(userProfileDetails?.first_name),
    lastName: Boolean(userProfileDetails?.last_name),
    email: Boolean(userProfileDetails?.email),
    phoneNumber: Boolean(userProfileDetails?.mobileNumber),
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: userProfileValidationSchema,
    context: {
      enablePermanentAddress: enablePermanentAddress,
      enableBillingAddress: enableBillingAddress,
      billingSameAsPermanent: sameAsPermanent,
    },
    validateOnMount: true,
    enableReinitialize: true,

    onSubmit: async (values) => {
      let uploadedPhotoUrl = null;

      // Upload profile photo
      if (values.profile_photo) {
        try {
          setUploading(true);
          const formData = new FormData();
          formData.append("profilePhoto", values.profile_photo);

          const response = await uploadUserPhoto(formData);
          if (response.data?.success) {
            uploadedPhotoUrl = response.data.data.profile_photo;

            // Update Redux with new photo
            updateSessionDetails("user", { profile_photo: uploadedPhotoUrl });
          }
        } catch (err: any) {
          console.error("Photo upload failed", err);
          setSnackbarMessage(
            err?.response?.data?.message ||
            err?.response?.data?.data?.message ||
            "Photo upload failed."
          );
          setSnackbarOpen(true);
          return;
        } finally {
          setUploading(false);
        }
      }

      // Build user profile payload
      const payload = buildUserProfilePayload({
        values,
        sameAsPermanent,
        enablePermanentAddress,
        enableBillingAddress,
        disableLockedFields,
        userProfileDetails,
      });

      const { first_name, last_name, email, mobileNumber, isFuneralDirector } = payload;

      try {
        const response = await updateUserProfile(payload);
        if (
          response.data?.data?.message ===
          "The Email Address entered is already in use."
        ) {
          setSnackbarMessage(response.data?.data?.message);
          setSnackbarOpen(true);
          return;
        }

        updateSessionDetails("user", {
          first_name: first_name,
          last_name: last_name,
          email: email,
          mobileNumber: mobileNumber,
          isFuneralDirector: isFuneralDirector,
        });

        await fetchUserProfileDetails();
        setIsEditMode(false);
      } catch (error: any) {
        console.error("Profile update failed", error);
        setSnackbarMessage(
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          "Profile update failed."
        );
        setSnackbarOpen(true);
      }
    },
  });

  const shouldDisablePermanentCity = useMemo(() => {
    const { state, zipcode } = formik.values.permanentAddress;
    return !state?.trim() && !zipcode?.trim();
  }, [formik.values.permanentAddress.state, formik.values.permanentAddress.zipcode]);

  const shouldDisableBillingCity = useMemo(() => {
    const { state, zipcode } = formik.values.billingAddress;
    return !state?.trim() && !zipcode?.trim();
  }, [formik.values.billingAddress.state, formik.values.billingAddress.zipcode]);

  const permanentAddress = formik.values.permanentAddress as PermanentAddress;

  const isPermanentAddressFilled = Object.values(permanentAddress).every(
    (value) => value !== ""
  );

  const isPermanentAddressComplete = useMemo(() => {
    if (!enablePermanentAddress) return true;

    const addr = formik.values.permanentAddress;
    return Boolean(
      addr.street && addr.city && addr.state && addr.country && addr.zipcode
    );
  }, [enablePermanentAddress, formik.values.permanentAddress]);

  const isBillingAddressComplete = useMemo(() => {
    if (!enableBillingAddress || sameAsPermanent) return true;

    const addr = formik.values.billingAddress;
    return Boolean(
      addr.street && addr.city && addr.state && addr.country && addr.zipcode
    );
  }, [enableBillingAddress, sameAsPermanent, formik.values.billingAddress]);

  const canSubmit =
    formik.isValid &&
    formik.dirty &&
    isPermanentAddressComplete &&
    isBillingAddressComplete;
  // !emailAvailabilityError &&
  // !phoneAvailabilityError;

  console.log("CAN SUBMIT::", canSubmit);

  console.log("FORMIK.ERRORS::", formik.errors);

  // Sync billing to permanent when sameAsPermanent changes to true
  useEffect(() => {
    if (sameAsPermanent) {
      formik.setFieldValue("billingAddress", {
        ...formik.values.permanentAddress,
      });

      // mark billing fields as touched
      formik.setTouched({
        ...formik.touched,
        billingAddress: {
          street: true,
          city: true,
          state: true,
          country: true,
          zipcode: true,
        },
      });
    }
  }, [sameAsPermanent]);

  // const [debouncedEmail] = useDebounce(formik.values.email, 500);
  // const [debouncedPhone] = useDebounce(formik.values.phoneNumber, 500);

  /*
  useEffect(() => {
    const validateEmail = async () => {
      if (
        disableLockedFields.email ||
        !debouncedEmail ||
        formik.errors.email
      ) {
        setEmailAvailabilityError("");
        return;
      }
      try {
        const res = await checkExistingUser({
          email: debouncedEmail,
          type: "email",
        });
        if (res.data?.data?.userExists) {
          setEmailAvailabilityError("This email is already in use.");
        } else {
          setEmailAvailabilityError("");
        }
      } catch (err: any) {
        setEmailAvailabilityError("");
      }
    };
    validateEmail();
  }, [debouncedEmail, formik.errors.email, disableLockedFields.email]);
  */

  /*
  useEffect(() => {
    const validatePhone = async () => {
      const filteredPhone = debouncedPhone.replace(/\D/g, "");
      if (
        disableLockedFields.phoneNumber ||
        filteredPhone.length !== 10 ||
        formik.errors.phoneNumber
      ) {
        setPhoneAvailabilityError("");
        return;
      }
      try {
        const res = await checkExistingUser({
          mobileNumber: filteredPhone,
          type: "mobile",
        });
        if (res.data?.data?.userExists) {
          setPhoneAvailabilityError("This mobile number is already in use.");
        } else {
          setPhoneAvailabilityError("");
        }
      } catch (err: any) {
        setPhoneAvailabilityError("");
      }
    };
    validatePhone();
  }, [
    debouncedPhone,
    formik.errors.phoneNumber,
    disableLockedFields.phoneNumber,
  ]);
  */

  // Permanent Address Options and Maps
  const permanentCityOptions = useMemo<Option[]>(() => {
    const state = formik.values.permanentAddress.state;
    const zipcode = formik.values.permanentAddress.zipcode;

    if (state && zipcode) {
      return filterOptionsByValue(
        addressData,
        "State",
        state,
        "Code",
        zipcode
      ).cityOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (state) {
      return filterOptionsByValue(addressData, "State", state).cityOptions.sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    }
    if (zipcode) {
      return filterOptionsByValue(
        addressData,
        "Code",
        zipcode
      ).cityOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    return allAddressOptions.cityOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [
    formik.values.permanentAddress.zipcode,
    formik.values.permanentAddress.state,
  ]);

  const permanentZipOptions = useMemo<Option[]>(() => {
    const city = formik.values.permanentAddress.city;
    const state = formik.values.permanentAddress.state;

    if (state && city) {
      return filterOptionsByValue(
        addressData,
        "City",
        city,
        "State",
        state
      ).postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (state) {
      return filterOptionsByValue(
        addressData,
        "State",
        state
      ).postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (city) {
      return filterOptionsByValue(
        addressData,
        "City",
        city
      ).postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    return allAddressOptions.postCodeOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [
    formik.values.permanentAddress.city,
    formik.values.permanentAddress.state,
  ]);

  const permanentStateOptions = useMemo<Option[]>(() => {
    const city = formik.values.permanentAddress.city;
    const zipCode = formik.values.permanentAddress.zipcode;

    if (city && zipCode) {
      return filterOptionsByValue(
        addressData,
        "City",
        city,
        "Code",
        zipCode
      ).stateOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (city) {
      return filterOptionsByValue(addressData, "City", city).stateOptions.sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    }
    if (zipCode) {
      return filterOptionsByValue(
        addressData,
        "Code",
        zipCode
      ).stateOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    return allAddressOptions.stateOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [
    formik.values.permanentAddress.city,
    formik.values.permanentAddress.zipcode,
  ]);

  const permanentCityByValue = useMemo(
    () => new Map(permanentCityOptions.map((o) => [o.value, o])),
    [permanentCityOptions]
  );

  const permanentZipByValue = useMemo(
    () => new Map(permanentZipOptions.map((o) => [o.value, o])),
    [permanentZipOptions]
  );

  const permanentStateByValue = useMemo(
    () => new Map(permanentStateOptions.map((o) => [o.value, o])),
    [permanentStateOptions]
  );

  // Billing Address Options and Maps
  const billingCityOptions = useMemo<Option[]>(() => {
    const state = formik.values.billingAddress.state;
    const zipcode = formik.values.billingAddress.zipcode;

    if (state && zipcode) {
      return filterOptionsByValue(
        addressData,
        "State",
        state,
        "Code",
        zipcode
      ).cityOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (state) {
      return filterOptionsByValue(addressData, "State", state).cityOptions.sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    }
    if (zipcode) {
      return filterOptionsByValue(
        addressData,
        "Code",
        zipcode
      ).cityOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    return allAddressOptions.cityOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [
    formik.values.billingAddress.zipcode,
    formik.values.billingAddress.state,
  ]);

  const billingZipOptions = useMemo<Option[]>(() => {
    const city = formik.values.billingAddress.city;
    const state = formik.values.billingAddress.state;

    if (state && city) {
      return filterOptionsByValue(
        addressData,
        "City",
        city,
        "State",
        state
      ).postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (state) {
      return filterOptionsByValue(
        addressData,
        "State",
        state
      ).postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (city) {
      return filterOptionsByValue(
        addressData,
        "City",
        city
      ).postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    return allAddressOptions.postCodeOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [formik.values.billingAddress.city, formik.values.billingAddress.state]);

  const billingStateOptions = useMemo<Option[]>(() => {
    const city = formik.values.billingAddress.city;
    const zipCode = formik.values.billingAddress.zipcode;

    if (city && zipCode) {
      return filterOptionsByValue(
        addressData,
        "City",
        city,
        "Code",
        zipCode
      ).stateOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (city) {
      return filterOptionsByValue(addressData, "City", city).stateOptions.sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    }
    if (zipCode) {
      return filterOptionsByValue(
        addressData,
        "Code",
        zipCode
      ).stateOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    return allAddressOptions.stateOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [formik.values.billingAddress.city, formik.values.billingAddress.zipcode]);

  const billingCityByValue = useMemo(
    () => new Map(billingCityOptions.map((o) => [o.value, o])),
    [billingCityOptions]
  );

  const billingZipByValue = useMemo(
    () => new Map(billingZipOptions.map((o) => [o.value, o])),
    [billingZipOptions]
  );

  const billingStateByValue = useMemo(
    () => new Map(billingStateOptions.map((o) => [o.value, o])),
    [billingStateOptions]
  );

  // Permanent Handlers
  const handlePermanentCityChange = async (_: any, newValue: Option | null) => {
    if (newValue) {
      const { stateOptions, postCodeOptions } = filterOptionsByValue(
        addressData,
        "City",
        newValue.value
      );

      await formik.setFieldValue(
        "permanentAddress.city",
        newValue.value,
        false
      );

      formik.setFieldTouched("permanentAddress.city", true, true);

      if (stateOptions.length === 1) {
        formik.setFieldValue(
          "permanentAddress.state",
          stateOptions[0].value,
          true
        );
      } else {
        formik.setFieldValue("permanentAddress.state", "", true);
      }
      if (postCodeOptions.length === 1) {
        await formik.setFieldValue(
          "permanentAddress.zipcode",
          postCodeOptions[0].value,
          true
        );
        formik.setFieldTouched("permanentAddress.zipcode", true);
      } else {
        await formik.setFieldValue("permanentAddress.zipcode", "", false);
        formik.setFieldTouched("permanentAddress.zipcode", true);
      }
      // Sync to billing if sameAsPermanent
      if (sameAsPermanent) {
        await formik.setFieldValue("billingAddress", {
          ...formik.values.permanentAddress,
          city: newValue.value,
          state: stateOptions.length === 1 ? stateOptions[0].value : "",
          zipcode: postCodeOptions.length === 1 ? postCodeOptions[0].value : "",
        });
      }
    } else {
      await formik.setFieldValue("permanentAddress.city", "", false);
      await formik.setFieldValue("permanentAddress.zipcode", "", false);
      formik.setFieldTouched("permanentAddress.city", true);
      formik.setFieldTouched("permanentAddress.zipcode", true);
      // Sync to billing if sameAsPermanent
      if (sameAsPermanent) {
        await formik.setFieldValue("billingAddress", {
          ...formik.values.permanentAddress,
          city: "",
          zipcode: "",
        });
      }
    }
    formik.setFieldTouched("permanentAddress.city", true, true);
    await formik.validateForm();
  };

  const handlePermanentZipcodeChange = async (
    _: any,
    newValue: Option | null
  ) => {
    if (newValue) {
      const { cityOptions, stateOptions } = filterOptionsByValue(
        addressData,
        "Code",
        newValue.value
      );

      await formik.setFieldValue(
        "permanentAddress.zipcode",
        newValue.value,
        false
      );
      formik.setFieldTouched("permanentAddress.zipcode", true);

      if (cityOptions.length === 1) {
        await formik.setFieldValue(
          "permanentAddress.city",
          cityOptions[0].value,
          false
        );
      } else {
        formik.setFieldValue("permanentAddress.city", "", true);
      }
      if (stateOptions.length === 1) {
        await formik.setFieldValue(
          "permanentAddress.state",
          stateOptions[0].value,
          false
        );
      } else {
        formik.setFieldValue("permanentAddress.state", "", true);
        formik.setFieldTouched("permanentAddress.city", true);
      }
      // Sync to billing if sameAsPermanent
      if (sameAsPermanent) {
        await formik.setFieldValue("billingAddress", {
          ...formik.values.permanentAddress,
          zipcode: newValue.value,
          city: cityOptions.length === 1 ? cityOptions[0].value : "",
          state: stateOptions.length === 1 ? stateOptions[0].value : "",
        });
      }
    } else {
      await formik.setFieldValue("permanentAddress.zipcode", "", false);
      formik.setFieldTouched("permanentAddress.zipcode", true);
      // Sync to billing if sameAsPermanent
      if (sameAsPermanent) {
        await formik.setFieldValue("billingAddress", {
          ...formik.values.permanentAddress,
          zipcode: "",
        });
      }
    }
    formik.setFieldTouched("permanentAddress.zipcode", true, true);
    await formik.validateForm();
  };

  const handlePermanentStateChange = async (
    _: any,
    newValue: Option | null
  ) => {
    if (newValue) {
      const { postCodeOptions } = filterOptionsByValue(
        addressData,
        "State",
        newValue.value
      );
      formik.setFieldValue("permanentAddress.state", newValue.value, true);
      formik.setFieldTouched("permanentAddress.state", true, true);

      if (postCodeOptions.length === 1) {
        formik.setFieldValue(
          "permanentAddress.zipcode",
          postCodeOptions[0].value,
          true
        );
      } else {
        formik.setFieldValue("permanentAddress.zipcode", "", true);
      }
      // Sync to billing if sameAsPermanent
      if (sameAsPermanent) {
        await formik.setFieldValue("billingAddress", {
          ...formik.values.permanentAddress,
          state: newValue.value,
          zipcode: postCodeOptions.length === 1 ? postCodeOptions[0].value : "",
        });
      }
    } else {
      await formik.setFieldValue("permanentAddress.state", "", false);
      await formik.setFieldValue("permanentAddress.zipcode", "", false);
      await formik.setFieldValue("permanentAddress.city", "", false);
      formik.setFieldValue("permanentAddress.state", "", true);
      formik.setFieldTouched("permanentAddress.zipcode", true);
      formik.setFieldTouched("permanentAddress.city", true);
      // Sync to billing if sameAsPermanent
      if (sameAsPermanent) {
        await formik.setFieldValue("billingAddress", {
          ...formik.values.permanentAddress,
          state: "",
          zipcode: "",
          city: "",
        });
      }
    }
    formik.setFieldTouched("permanentAddress.state", true, true);
    await formik.validateForm();
  };

  // Billing Handlers
  const handleBillingCityChange = async (_: any, newValue: Option | null) => {
    if (newValue) {
      const { stateOptions, postCodeOptions } = filterOptionsByValue(
        addressData,
        "City",
        newValue.value
      );

      await formik.setFieldValue("billingAddress.city", newValue.value, false);
      if (stateOptions.length === 1) {
        formik.setFieldValue(
          "billingAddress.state",
          stateOptions[0].value,
          true
        );
      } else {
        formik.setFieldValue("billingAddress.state", "", true);
      }
      if (postCodeOptions.length === 1) {
        await formik.setFieldValue(
          "billingAddress.zipcode",
          postCodeOptions[0].value,
          true
        );
        formik.setFieldTouched("billingAddress.zipcode", true);
      } else {
        await formik.setFieldValue("billingAddress.zipcode", "", false);
        formik.setFieldTouched("billingAddress.zipcode", true);
      }
    } else {
      await formik.setFieldValue("billingAddress.city", "", false);
      await formik.setFieldValue("billingAddress.zipcode", "", false);
      formik.setFieldTouched("billingAddress.city", true);
      formik.setFieldTouched("billingAddress.zipcode", true);
    }
    formik.setFieldTouched("billingAddress.city", true, true);
    await formik.validateForm();
  };

  const handleBillingZipcodeChange = async (
    _: any,
    newValue: Option | null
  ) => {
    if (newValue) {
      const { cityOptions, stateOptions } = filterOptionsByValue(
        addressData,
        "Code",
        newValue.value
      );

      await formik.setFieldValue(
        "billingAddress.zipcode",
        newValue.value,
        false
      );
      formik.setFieldTouched("billingAddress.zipcode", true);

      if (cityOptions.length === 1) {
        await formik.setFieldValue(
          "billingAddress.city",
          cityOptions[0].value,
          false
        );
      } else {
        formik.setFieldValue("billingAddress.city", "", true);
      }
      if (stateOptions.length === 1) {
        await formik.setFieldValue(
          "billingAddress.state",
          stateOptions[0].value,
          false
        );
      } else {
        formik.setFieldValue("billingAddress.state", "", true);
        formik.setFieldTouched("billingAddress.city", true);
      }
    } else {
      await formik.setFieldValue("billingAddress.zipcode", "", false);
      formik.setFieldTouched("billingAddress.zipcode", true);
    }
    formik.setFieldTouched("billingAddress.zipcode", true, true);
    await formik.validateForm();
  };

  const handleBillingStateChange = async (_: any, newValue: Option | null) => {
    if (newValue) {
      const { postCodeOptions } = filterOptionsByValue(
        addressData,
        "State",
        newValue.value
      );
      formik.setFieldValue("billingAddress.state", newValue.value, true);
      if (postCodeOptions.length === 1) {
        formik.setFieldValue(
          "billingAddress.zipcode",
          postCodeOptions[0].value,
          true
        );
      } else {
        formik.setFieldValue("billingAddress.zipcode", "", true);
      }
    } else {
      await formik.setFieldValue("billingAddress.state", "", false);
      await formik.setFieldValue("billingAddress.zipcode", "", false);
      await formik.setFieldValue("billingAddress.city", "", false);
      formik.setFieldValue("billingAddress.state", "", true);
      formik.setFieldTouched("billingAddress.zipcode", true);
      formik.setFieldTouched("billingAddress.city", true);
    }
    formik.setFieldTouched("billingAddress.state", true, true);
    await formik.validateForm();
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setSameAsPermanent(checked);

    if (checked) {
      formik.setFieldValue("billingAddress", {
        ...formik.values.permanentAddress,
      });
      setEnableBillingAddress(false); // disable billing fields when sameAsPermanent
    } else {
      setEnableBillingAddress(true); // re-enable if unchecked
      // Reset billing address fields to empty when unchecked
      formik.setFieldValue("billingAddress", {
        street: "",
        city: "",
        state: "",
        country: "",
        zipcode: "",
      });
    }
    formik.validateForm();
  };

  const handlePermanentAddressChange = (e) => {
    const { name, value } = e.target;

    // Update permanent address
    formik.setFieldValue(`permanentAddress.${name}`, value);
    formik.setFieldTouched(`permanentAddress.${name}`, true, false);

    // Sync billing address if sameAsPermanent is checked
    if (sameAsPermanent) {
      formik.setFieldValue(`billingAddress.${name}`, value);
      formik.setFieldTouched(`billingAddress.${name}`, true, false);
    }
  };

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    formik.setFieldValue(`billingAddress.${name}`, value);
    formik.setFieldTouched(`billingAddress.${name}`, true, false);
  };

  const handleAlphaChange = (e, addressType = null) => {
    const { name, value } = e.target;

    const filtered = value.replace(/[^A-Za-z ]/g, "").replace(/\s+/g, " ");

    if (addressType) {
      formik.setFieldValue(`${addressType}.${name}`, filtered);
      formik.setFieldTouched(`${addressType}.${name}`, true, false);
    } else {
      formik.setFieldValue(name, filtered);
      formik.setFieldTouched(name, true, false);
    }
  };

  const handleZipChange = (e, addressType) => {
    const { name, value } = e.target;
    const filtered = value.replace(/\D/g, "").slice(0, 5);

    formik.setFieldValue(`${addressType}.${name}`, filtered);
    formik.setFieldTouched(`${addressType}.${name}`, true, false);
  };

  const handleDigitChange = (e) => {
    const { name, value } = e.target;
    const filtered = value.replace(/\D/g, "");
    const maxLength = 10;

    formik.setFieldValue(name, filtered.slice(0, maxLength));
    formik.setFieldTouched(name, true, false);
  };

  const handleCancel = () => {
    setIsEditMode((prev: boolean) => !prev);
  };

  // LOCAL STORAGE USER DETAILS
  const userDetails = localStorage.getItem("userDetails");
  let parsedUserDetails: any = null;
  try {
    if (userDetails) parsedUserDetails = JSON.parse(userDetails);
  } catch (error) {
    console.error("Failed to parse userDetails:", error);
  }

  const handleDeletePhoto = async () => {
    try {
      const response = await deleteUserPhoto();
      if (response.data?.success) {
        // update Redux state
        dispatch({
          type: "SESSION:UPDATE_USER_DETAILS",
          payload: {
            profile_photo: null,
          },
        });

        // update localStorage
        if (parsedUserDetails && parsedUserDetails?.user) {
          parsedUserDetails.user.profile_photo = "";
          localStorage.setItem(
            "userDetails",
            JSON.stringify(parsedUserDetails)
          );
        }
      }
    } catch (error) {
      console.error("ERROR DELETE PHOTO::", error);
    }
  };

  const buildUserProfilePayload = ({
    values,
    sameAsPermanent,
    enablePermanentAddress,
    enableBillingAddress,
    disableLockedFields,
    userProfileDetails,
  }) => {
    const payload: any = {
      first_name: disableLockedFields.firstName
        ? userProfileDetails.first_name
        : values.firstName,

      last_name: disableLockedFields.lastName
        ? userProfileDetails.last_name
        : values.lastName,

      email: disableLockedFields.email
        ? userProfileDetails.email
        : values.email,

      mobileNumber: disableLockedFields.phoneNumber
        ? userProfileDetails.mobileNumber
        : values.phoneNumber,

      isFuneralDirector: values.isFuneralDirector === "IamaFuneralDirector",
    };

    // Permanent Address
    if (enablePermanentAddress) {
      payload.address = {
        ...values.permanentAddress,
      };
    }

    // Billing Address
    payload.sameAsPermanent = sameAsPermanent;

    if (sameAsPermanent) {
      payload.billingAddress = {
        ...values.permanentAddress,
      };
    } else if (enableBillingAddress) {
      payload.billingAddress = {
        ...values.billingAddress,
      };
    } else {
      payload.billingAddress = null;
    }

    return payload;
  };

  const renderLabel = (text: string, required = false) => (
    <Typography className="user-profile-form__label">
      {text}
      {required && <span className="user-profile-form__asterisk">*</span>}
    </Typography>
  );

  return (
    <Paper className="user-profile-form">
      <form onSubmit={formik.handleSubmit}>
        {/* 1. User Profile Section */}
        <div className="user-profile-form__section">
          <Typography
            variant="h6"
            className="user-profile-form__heading-profile"
          >
            User Profile
          </Typography>
          <div className="user-profile-form__photo">
            <Typography className="user-profile-form__profile-text">
              Profile Photo (Optional)
            </Typography>

            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.svg,image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                // Allowed file types
                const allowedTypes = [
                  "image/jpeg",
                  "image/jpg",
                  "image/png",
                  "image/webp",
                  "image/svg+xml",
                ];

                if (!allowedTypes.includes(file.type)) {
                  alert(
                    "Invalid file type. Only .jpg, .jpeg, .png, .webp, .svg files are allowed."
                  );
                  e.target.value = "";
                  return;
                }

                // Max file size 10MB
                const maxSizeMB = 10;
                if (file.size / 1024 / 1024 > maxSizeMB) {
                  alert(
                    `File is too large. Maximum allowed size is ${maxSizeMB} MB.`
                  );
                  e.target.value = "";
                  return;
                }

                // if valid, set preview & upload
                formik.setFieldValue("profile_photo", file);
                setPreview(URL.createObjectURL(file));
              }}
            />

            <Box
              className="user-profile-form__photo-left"
              onClick={() => fileInputRef.current.click()}
              style={{ cursor: "pointer" }}
            >
              <Box
                sx={{
                  width: 95,
                  height: 95,
                  position: "relative",
                }}
              >
                <Box
                  className="user-profile-form__photo-icon"
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  {uploading ? (
                    <CircularProgress />
                  ) : preview ? (
                    <img
                      src={preview}
                      alt="Preview"
                      className="user-profile-form__photo-preview"
                    />
                  ) : (
                    <PhotoIcon />
                  )}
                </Box>

                {preview && (
                  <IconButton
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation();

                      const confirmDelete = window.confirm(
                        "Are you sure you want to remove your profile photo?"
                      );

                      if (!confirmDelete) return;

                      await handleDeletePhoto();
                      setPreview(null);
                      formik.setFieldValue("profile_photo", null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      backgroundColor: "#fff",
                      boxShadow: 2,
                      zIndex: 2,
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Typography className="user-profile-form__upload-text">
                {preview ? "Change Photo" : "Upload Photo"}
              </Typography>
            </Box>
          </div>

          <Grid container spacing={2} className="user-profile-form__grid">
            <Grid item xs={12} md={6}>
              {renderLabel("First Name", true)}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter First Name"
                size="small"
                name="firstName"
                value={formik.values.firstName}
                onChange={handleAlphaChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
                helperText={formik.touched.firstName && formik.errors.firstName}
                disabled={disableLockedFields.firstName}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabel("Last Name", true)}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter Last Name"
                size="small"
                name="lastName"
                value={formik.values.lastName}
                onChange={handleAlphaChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
                helperText={formik.touched.lastName && formik.errors.lastName}
                disabled={disableLockedFields.lastName}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabel("Email Address", true)}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter Email Address"
                size="small"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.email &&
                  (Boolean(formik.errors.email)
                    // || Boolean(emailAvailabilityError)
                  )
                }
                helperText={
                  formik.touched.email &&
                  (formik.errors.email
                    // || emailAvailabilityError
                  )
                }
                disabled={disableLockedFields.email}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabel("Phone Number", true)}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter Phone Number"
                size="small"
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={handleDigitChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.phoneNumber &&
                  (Boolean(formik.errors.phoneNumber)
                    // || Boolean(phoneAvailabilityError)
                  )
                }
                helperText={
                  formik.touched.phoneNumber &&
                  (formik.errors.phoneNumber
                    // || phoneAvailabilityError
                  )
                }
                disabled={disableLockedFields.phoneNumber}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography className="user-profile-form__label">
                  Funeral Director<span className="user-profile-form__asterisk">*</span>
                </Typography>
                <FormControl
                  fullWidth
                  variant="outlined"
                  className="form-field half-width"
                  error={formik.touched.isFuneralDirector && Boolean(formik.errors.isFuneralDirector)}
                >
                  <Select
                    name="isFuneralDirector"
                    value={formik.values.isFuneralDirector}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: '#9e9e9e' }}>Select an option</span>;
                      }
                      const menuItem = [
                        { value: "BuyingforSelf/Family", label: "Buying for Self/Family" },
                        { value: "IamaFuneralDirector", label: "I am a Funeral Director" }
                      ].find(item => item.value === selected);
                      return menuItem ? menuItem.label : selected;
                    }}
                    required
                  >
                    <MenuItem value="BuyingforSelf/Family">Buying for Self/Family</MenuItem>
                    <MenuItem value="IamaFuneralDirector">I am a Funeral Director</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </div>

        {/* 2. Permanent Address Section */}
        <div className="user-profile-form__section">
          <Box className="user-profile-form__heading-box">
            <Typography variant="h6" className="user-profile-form__heading">
              Permanent Address (Optional)
            </Typography>
            <FormControlLabel
              control={
                <AntSwitch
                  checked={enablePermanentAddress}
                  onChange={(e) => {
                    const value = e.target.checked;
                    setEnablePermanentAddress(value);

                    if (value) {
                      formik.setTouched({
                        ...formik.touched,
                        permanentAddress: {
                          street: true,
                          city: true,
                          state: true,
                          country: true,
                          zipcode: true,
                        },
                      });
                    }

                    formik.validateForm();
                  }}
                  inputProps={{ "aria-label": "ant design" }}
                  sx={{ marginRight: "0.3rem" }}
                  checkedColor="#15585D"
                />
              }
              label={"Add"}
              labelPlacement="end"
            />
          </Box>
          {renderLabel("Street/Area", enablePermanentAddress)}

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter Street Address"
            size="small"
            name="street"
            className="billing-details__field"
            value={formik.values.permanentAddress.street}
            onChange={handlePermanentAddressChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.permanentAddress?.street &&
              Boolean(formik.errors.permanentAddress?.street)
            }
            helperText={
              formik.touched.permanentAddress?.street &&
              formik.errors.permanentAddress?.street
            }
            className="user-profile-form__input--full"
            disabled={!enablePermanentAddress}
          />
          <Grid container spacing={2}>
            {/* City */}
            <Grid item xs={12} md={6}>
              {renderLabel("City", enablePermanentAddress)}
              <Autocomplete
                options={permanentCityOptions}
                getOptionLabel={(option) => option.label}
                value={
                  permanentCityByValue.get(
                    formik.values.permanentAddress.city
                  ) || null
                }
                onChange={handlePermanentCityChange}
                onBlur={formik.handleBlur}
                filterOptions={limitFilterOptions}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                openOnFocus
                disabled={!enablePermanentAddress || shouldDisablePermanentCity}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select City"
                    error={
                      formik.touched.permanentAddress?.city &&
                      Boolean(formik.errors.permanentAddress?.city)
                    }
                    className="billing-details__field"
                    helperText={
                      formik.touched.permanentAddress?.city &&
                      formik.errors.permanentAddress?.city
                    }
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: 0,
                  },
                }}
              />
            </Grid>
            {/* State */}
            <Grid item xs={12} md={6}>
              {renderLabel("State", enablePermanentAddress)}
              <Autocomplete
                options={permanentStateOptions}
                getOptionLabel={(option) => option.label}
                value={
                  permanentStateByValue.get(
                    formik.values.permanentAddress.state
                  ) || null
                }
                onChange={handlePermanentStateChange}
                onBlur={formik.handleBlur}
                filterOptions={limitFilterOptions}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                openOnFocus
                disabled={!enablePermanentAddress}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select State"
                    error={
                      formik.touched.permanentAddress?.state &&
                      Boolean(formik.errors.permanentAddress?.state)
                    }
                    className="billing-details__field"
                    helperText={
                      formik.touched.permanentAddress?.state &&
                      formik.errors.permanentAddress?.state
                    }
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: 0,
                  },
                }}
              />
            </Grid>
            {/* Country */}
            <Grid item xs={12} md={6}>
              {renderLabel("Country", enablePermanentAddress)}
              <TextField
                select
                fullWidth
                variant="outlined"
                size="small"
                name="country"
                className="billing-details__field"
                value={formik.values.permanentAddress.country}
                onChange={(e) => {
                  const val = e.target.value;
                  formik.setFieldValue("permanentAddress.country", val);
                  formik.setFieldTouched(
                    "permanentAddress.country",
                    true,
                    true
                  );
                  if (sameAsPermanent) {
                    formik.setFieldValue("billingAddress.country", val);
                    formik.setFieldTouched(
                      "billingAddress.country",
                      true,
                      true
                    );
                  }
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.permanentAddress?.country &&
                  Boolean(formik.errors.permanentAddress?.country)
                }
                helperText={
                  formik.touched.permanentAddress?.country &&
                  formik.errors.permanentAddress?.country
                }
                disabled={!enablePermanentAddress}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#9e9e9e" }}>
                          -- Select Country --
                        </span>
                      );
                    }
                    return selected;
                  },
                }}
              >
                <MenuItem value="" disabled>
                  -- Select Country --
                </MenuItem>
                <MenuItem value="USA">USA</MenuItem>
              </TextField>
            </Grid>
            {/* Zipcode */}
            <Grid item xs={12} md={6}>
              {renderLabel("Zipcode", enablePermanentAddress)}
              <Autocomplete
                options={permanentZipOptions}
                getOptionLabel={(option) => option.label}
                value={
                  permanentZipByValue.get(
                    formik.values.permanentAddress.zipcode
                  ) || null
                }
                onChange={handlePermanentZipcodeChange}
                onBlur={formik.handleBlur}
                filterOptions={limitFilterOptions}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                openOnFocus
                disabled={!enablePermanentAddress}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Zipcode"
                    className="billing-details__field"
                    error={
                      formik.touched.permanentAddress?.zipcode &&
                      Boolean(formik.errors.permanentAddress?.zipcode)
                    }
                    helperText={
                      formik.touched.permanentAddress?.zipcode &&
                      formik.errors.permanentAddress?.zipcode
                    }
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: 0,
                  },
                }}
              />
            </Grid>
          </Grid>
          <FormControlLabel
            control={
              <Checkbox
                checked={sameAsPermanent}
                onChange={(e) => {
                  // disable checkbox if permanent address not toggled
                  if (!isPermanentAddressFilled) {
                    setSnackbarMessage(
                      "Please complete Permanent Address first"
                    );
                    setSnackbarOpen(true);
                    return;
                  }
                  handleCheckboxChange(e);
                  formik.setFieldValue(
                    "billingSameAsPermanent",
                    e.target.checked
                  );
                  formik.validateForm();
                }}
                sx={{
                  "&.Mui-checked": {
                    color: "#0B4F4A",
                  },
                }}
              />
            }
            label="Billing address is same as permanent address"
            className="user-profile-form__checkbox"
          />
        </div>

        {/* 3. Billing Address Section */}
        <div className="user-profile-form__section">
          <Box className="user-profile-form__heading-box">
            <Typography variant="h6" className="user-profile-form__heading">
              Billing Address (Optional)
            </Typography>
            <FormControlLabel
              control={
                <AntSwitch
                  checked={enableBillingAddress}
                  onChange={(e) => {
                    const value = e.target.checked;
                    setEnableBillingAddress(value);
                    if (value) {
                      formik.setTouched({
                        ...formik.touched,
                        billingAddress: {
                          street: true,
                          city: true,
                          state: true,
                          country: true,
                          zipcode: true,
                        },
                      });
                    }

                    formik.validateForm();
                  }}
                  inputProps={{ "aria-label": "ant design" }}
                  sx={{ marginRight: "0.3rem" }}
                  checkedColor="#15585D"
                  disabled={sameAsPermanent}
                />
              }
              label={"Add"}
              labelPlacement="end"
            />
          </Box>

          {renderLabel("Street/Area", enableBillingAddress && !sameAsPermanent)}

          <TextField
            fullWidth
            variant="outlined"
            size="small"
            name="street"
            placeholder="Enter Street Address"
            className="billing-details__field"
            value={formik.values.billingAddress.street}
            onChange={handleBillingChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.billingAddress?.street &&
              Boolean(formik.errors.billingAddress?.street)
            }
            helperText={
              formik.touched.billingAddress?.street &&
              formik.errors.billingAddress?.street
            }
            disabled={sameAsPermanent || !enableBillingAddress}
            className="user-profile-form__input--full"
          />
          <Grid container spacing={2}>
            {/* City */}
            <Grid item xs={12} md={6}>
              {renderLabel("City", enableBillingAddress && !sameAsPermanent)}
              <Autocomplete
                options={billingCityOptions}
                getOptionLabel={(option) => option.label}
                value={
                  billingCityByValue.get(formik.values.billingAddress.city) ||
                  null
                }
                onChange={handleBillingCityChange}
                onBlur={formik.handleBlur}
                filterOptions={limitFilterOptions}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                openOnFocus
                disabled={sameAsPermanent || !enableBillingAddress || shouldDisableBillingCity}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select City"
                    className="billing-details__field"
                    error={
                      formik.touched.billingAddress?.city &&
                      Boolean(formik.errors.billingAddress?.city)
                    }
                    helperText={
                      formik.touched.billingAddress?.city &&
                      formik.errors.billingAddress?.city
                    }
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: 0,
                  },
                }}
              />
            </Grid>
            {/* State */}
            <Grid item xs={12} md={6}>
              {renderLabel("State", enableBillingAddress && !sameAsPermanent)}
              <Autocomplete
                options={billingStateOptions}
                getOptionLabel={(option) => option.label}
                value={
                  billingStateByValue.get(formik.values.billingAddress.state) ||
                  null
                }
                onChange={handleBillingStateChange}
                onBlur={formik.handleBlur}
                filterOptions={limitFilterOptions}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                openOnFocus
                disabled={sameAsPermanent || !enableBillingAddress}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select State"
                    className="billing-details__field"
                    error={
                      formik.touched.billingAddress?.state &&
                      Boolean(formik.errors.billingAddress?.state)
                    }
                    helperText={
                      formik.touched.billingAddress?.state &&
                      formik.errors.billingAddress?.state
                    }
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: 0,
                  },
                }}
              />
            </Grid>
            {/* Country */}
            <Grid item xs={12} md={6}>
              {renderLabel("Country", enableBillingAddress && !sameAsPermanent)}
              <TextField
                select
                fullWidth
                variant="outlined"
                size="small"
                name="country"
                className="billing-details__field"
                value={formik.values.billingAddress.country}
                onChange={(e) => {
                  formik.setFieldValue(
                    "billingAddress.country",
                    e.target.value
                  );
                  formik.setFieldTouched("billingAddress.country", true, true);
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.billingAddress?.country &&
                  Boolean(formik.errors.billingAddress?.country)
                }
                helperText={
                  formik.touched.billingAddress?.country &&
                  formik.errors.billingAddress?.country
                }
                disabled={sameAsPermanent || !enableBillingAddress}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#9e9e9e" }}>
                          -- Select Country --
                        </span>
                      );
                    }
                    return selected;
                  },
                }}
              >
                <MenuItem value="" disabled>
                  -- Select Country --
                </MenuItem>
                <MenuItem value="USA">USA</MenuItem>
              </TextField>
            </Grid>
            {/* Zipcode */}
            <Grid item xs={12} md={6}>
              {renderLabel("Zipcode", enableBillingAddress && !sameAsPermanent)}
              <Autocomplete
                options={billingZipOptions}
                getOptionLabel={(option) => option.label}
                value={
                  billingZipByValue.get(formik.values.billingAddress.zipcode) ||
                  null
                }
                onChange={handleBillingZipcodeChange}
                onBlur={formik.handleBlur}
                filterOptions={limitFilterOptions}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                openOnFocus
                disabled={sameAsPermanent || !enableBillingAddress}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Zipcode"
                    className="billing-details__field"
                    error={
                      formik.touched.billingAddress?.zipcode &&
                      Boolean(formik.errors.billingAddress?.zipcode)
                    }
                    helperText={
                      formik.touched.billingAddress?.zipcode &&
                      formik.errors.billingAddress?.zipcode
                    }
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: 0,
                  },
                }}
              />
            </Grid>
          </Grid>
        </div>

        {/* Buttons aligned to right */}
        <Box className="user-profile-form__buttons">
          <Button
            className="user-profile-form__cancel"
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!canSubmit}
            className={`user-profile-form__submit ${canSubmit ? "enabled" : "disabled"
              }`}
          >
            {isProfileCreated ? "Save Changes" : "Save"}
          </Button>
        </Box>
      </form>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="warning"
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            "& .MuiAlert-message": {
              padding: "8px 0",
            },
            "& .MuiAlert-icon": {
              display: "flex",
              alignItems: "center",
              padding: 0,
            },
            "& .MuiAlert-action": {
              display: "flex",
              alignItems: "center",
              padding: 0,
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default UserProfileForm;
